<template>
  <div>
    <button
      type="button"
      :class="['btn btn-primary btn-with-addon']"
      @click="showModal"
    >
      <span class="btn-addon">
        <i class="btn-addon-icon fe fe-plus-circle" />
      </span>
      Application Form
    </button>
    <a-modal
      :width="
        this.settings['isMobileView']
          ? 420
          : this.settings['isTabletView']
          ? 600
          : 840
      "
      v-model="visible"
      title="Add Application Form"
    >
      <template slot="footer">
        <a-button key="back" @click="handleCancel">Cancel</a-button>
        <a-button
          key="submit"
          type="primary"
          :loading="loading"
          @click="handleSubmit"
          >Create Application Form</a-button
        >
      </template>

      <a-form class="mb-0" :form="form" @submit="handleSubmit">
        <p class="text-uppercase font-weight-bold text-muted">
          Application form details
        </p>
        <div class="row mb-2">
          <div class="col-md-3 mb-2">
            <a-form-item label="For Session">
              <a-spin :spinning="sessionSpinning">
                <a-select
                  v-decorator="[
                    'session_id',
                    {
                      rules: [
                        { required: true, message: 'Please select a session!' },
                      ],
                      initialValue: currentSession ? currentSession.id : '',
                    },
                  ]"
                  placeholder="Select Class"
                  @change="handleSessionChange"
                >
                  <a-select-option
                    v-for="schoolSession in schoolSessions"
                    :value="schoolSession.id"
                    :key="schoolSession.id"
                  >
                    {{ schoolSession.name }}
                    <small class="text-primary ml-2">{{
                      currentSession.id === schoolSession.id ? "Current" : ""
                    }}</small>
                  </a-select-option>
                </a-select>
              </a-spin>
            </a-form-item>
          </div>
          <div class="col-md-3 mb-2">
            <a-form-item label="For Class">
              <a-spin :spinning="classSpinning">
                <a-select
                  v-decorator="[
                    'class_id',
                    {
                      rules: [
                        { required: true, message: 'Please select a class!' },
                      ],
                    },
                  ]"
                  placeholder="Select Class"
                  @change="handleClassChange"
                >
                  <a-select-option
                    v-for="schoolClass in schoolClasses"
                    :value="schoolClass.id"
                    :key="schoolClass.id"
                  >
                    {{ schoolClass.name }}
                  </a-select-option>
                </a-select>
              </a-spin>
            </a-form-item>
          </div>
          <div class="col-md-3 mb-2">
            <a-form-item label="Start Date">
              <a-date-picker
                v-decorator="[
                  'start_date',
                  {
                    rules: [
                      { required: true, message: 'Please input a start date' },
                    ],
                    initialValue: moment(),
                  },
                ]"
                :format="dateFormat"
              />
            </a-form-item>
          </div>
          <div class="col-md-3 mb-2">
            <a-form-item label="End Date">
              <a-date-picker
                v-decorator="[
                  'end_date',
                  {
                    rules: [
                      { required: true, message: 'Please input an end date' },
                    ],
                  },
                ]"
                :format="dateFormat"
              />
            </a-form-item>
          </div>
          <div class="col-md-3 mb-2">
            <a-form-item class="mb-0" label="Application Fee">
              <div style="width: 180px">
                <a-input-number
                  placeholder="Fee Amount"
                  v-decorator="[
                    `application_fee`,
                    {
                      rules: [
                        {
                          required: false,
                          message: 'Please input fee amount!',
                        },
                      ],
                    },
                  ]"
                  :initialValue="0"
                  :min="0"
                  :formatter="
                    (value) =>
                      `₹ ${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')
                  "
                  :parser="(value) => value.replace(/\₹\s?|(,*)/g, '')"
                  style="width: 100%"
                />
              </div>
            </a-form-item>
          </div>
          <div class="col-md-3 mb-2">
            <a-form-item label="Payment Mode">
              <a-select
                v-decorator="[
                  'payment_mode',
                  {
                    rules: [
                      {
                        required: form.getFieldValue('application_fee'),
                        message: 'Please select a payment mode!',
                      },
                    ],
                    initialValue: undefined,
                  },
                ]"
                placeholder="Select Payment mode"
              >
                <a-select-option :value="undefined">
                  Select Option
                </a-select-option>
                <a-select-option
                  v-for="onlineApplicationPaymentMode in onlineApplicationPaymentModes"
                  :value="onlineApplicationPaymentMode.value"
                  :key="onlineApplicationPaymentMode.value"
                >
                  {{ onlineApplicationPaymentMode.name }}
                </a-select-option>
              </a-select>
            </a-form-item>
          </div>
          <div class="col-md-3 mb-2">
            <a-form-item label="Enable online admission">
              <a-select
                v-decorator="[
                  'online_admission',
                  {
                    rules: [
                      { required: true, message: 'Please select an option!' },
                    ],
                    initialValue: 0,
                  },
                ]"
                placeholder="Select Class"
              >
                <a-select-option :value="0"> No </a-select-option>
                <a-select-option :value="1"> Yes </a-select-option>
              </a-select>
            </a-form-item>
          </div>
          <div class="col-md-3 mb-2">
            <a-form-item label="Admission Last Date">
              <a-date-picker
                v-decorator="[
                  'admission_last_date',
                  {
                    rules: [
                      {
                        required: form.getFieldValue('online_admission') === 1,
                        message: 'Please input a date',
                      },
                    ],
                  },
                ]"
                :format="dateFormat"
              />
            </a-form-item>
          </div>
          <div
            class="col-md-4 mb-2"
            v-if="
              form.getFieldValue('class_id') &&
              form.getFieldValue('online_admission') === 1
            "
          >
            <a-form-item label="Admission Fee">
              <a-spin :spinning="feeSpinning">
                <a-select
                  v-decorator="[
                    'admission_fee_id',
                    {
                      rules: [
                        {
                          required:
                            form.getFieldValue('online_admission') === 1,
                          message: 'Please select a fee!',
                        },
                      ],
                      initialValue: undefined,
                    },
                  ]"
                  placeholder="Select Admission Fee"
                >
                  <a-select-option :value="undefined">
                    Select Option
                  </a-select-option>
                  <a-select-option
                    v-for="classFee in classFees"
                    :value="classFee.id"
                    :key="classFee.id"
                  >
                    {{ classFee.fee.name }} ({{ classFee.fee_amount }})
                  </a-select-option>
                </a-select>
              </a-spin>
            </a-form-item>
          </div>
        </div>
        <p class="text-uppercase font-weight-bold text-muted">
          Subject groupings
        </p>
        <div class="row mb-4">
          <div
            class="col-12"
            v-for="(key, index) in subjectGroupings"
            :key="key"
          >
            <div class="d-flex">
              <div class="mr-2 pt-2">
                <span class="font-weight-bold">{{ index + 1 }}.</span>
              </div>
              <div class="col">
                <a-form-item>
                  <a-spin :spinning="subGroupSpinning">
                    <a-select
                      style="width: 100%"
                      v-decorator="[
                        `subject_group_master_id[${key}]`,
                        {
                          rules: [
                            {
                              required: true,
                              message: 'Please select a Subject Group!',
                            },
                          ],
                        },
                      ]"
                      placeholder="Select a group"
                    >
                      <a-select-option
                        v-for="group in subjectGroups"
                        :value="group.id"
                        :key="group.id"
                      >
                        {{ group.name }}
                      </a-select-option>
                    </a-select>
                  </a-spin>
                </a-form-item>
              </div>
              <div class="flex-grow-1">
                <a-form-item>
                  <a-select
                    v-decorator="[
                      `subject_group[${key}]`,
                      {
                        rules: [
                          { required: true, message: 'Please select subjects' },
                        ],
                        initialValue: [],
                      },
                    ]"
                    placeholer="Select subjects"
                    mode="multiple"
                    style="width: 100%"
                  >
                    <a-select-option
                      v-for="subject in subjects"
                      :key="subject.id"
                    >
                      {{ subject.name }}
                    </a-select-option>
                  </a-select>
                </a-form-item>
              </div>
              <div class="ml-2 pt-1">
                <button
                  class="btn btn-sm btn-danger"
                  @click.prevent="handleRemoveSubjectGrouping(key)"
                >
                  <i class="btn-addon-icon fe fe-trash-2" />
                </button>
              </div>
            </div>
          </div>
          <div class="col-12">
            <div class="border-top border-bottom">
              <button
                class="btn btn-primary btn-with-export"
                @click.prevent="handleAddSubjectGrouping"
              >
                + Add subject grouping
              </button>
            </div>
          </div>
        </div>
        <div class="row mb">
          <div class="col-12 mb-2">
            <a-form-item label="Selection criteria">
              <a-textarea
                v-decorator="[
                  'selection_criteria',
                  {
                    rules: [
                      {
                        required: false,
                        message: 'Please add a selection criteria!',
                      },
                    ],
                  },
                ]"
                placeholder="Enter selection criteria briefly.."
                :auto-size="{ minRows: 3, maxRows: 5 }"
              />
            </a-form-item>
          </div>
        </div>
      </a-form>
    </a-modal>
  </div>
</template>

<script>
import moment from 'moment'
import { find } from 'lodash'
import { mapState } from 'vuex'

import { onlineApplicationPaymentModes } from '@/services/globaldata.js'

export default {
  name: 'AddApplicationForm',
  mounted: function () {
    this.getSessions()
    this.getSubjects()
  },
  data: function () {
    return {
      form: this.$form.createForm(this),
      onlineApplicationPaymentModes,
      loading: false,
      visible: false,
      classSpinning: false,
      sessionSpinning: false,
      schoolClasses: [],
      schoolSessions: [],
      subjects: [],
      classFees: [],
      selectedSession: undefined,
      dateFormat: 'DD-MM-YYYY',
      subjectGroupings: [],
      groupingCounter: 0,
      subjectGroups: [],
      subGroupSpinning: false,
    }
  },
  computed: {
    currentSession: function () {
      const session = find(this.schoolSessions, ['is_current', 1])
      return session
    },
    ...mapState(['user', 'settings']),
  },
  watch: {
    currentSession: function (val) {
      this.selectedSession = val.id
    },
    subjectGroups: function (val) {
      return val
    },
  },
  methods: {
    moment,
    showModal: function () {
      this.visible = true
      this.getClasses()
    },
    getSessions: function () {
      this.sessionSpinning = true
      this.$http
        .get('/api/session')
        .then(({ data }) => {
          this.schoolSessions = data.data
          this.selectedSession = find(data.data, ['is_current', 1]).id
          this.getClasses()
          this.sessionSpinning = false
        })
        .catch((error) => {
          console.log(error)
        })
    },
    getSubjects: function () {
      this.$http
        .get('/api/subject')
        .then(({ data }) => {
          this.subjects = data.data
        })
        .catch((error) => {
          console.log(error)
        })
    },
    getClasses: function () {
      this.classSpinning = true
      this.$http
        .get('/api/class', {
          params: {
            session_id: this.selectedSession,
          },
        })
        .then(({ data }) => {
          this.schoolClasses = data.data
          this.getSubjectGroups()
          this.classSpinning = false
        })
        .catch((error) => {
          console.log(error)
        })
    },
    getSubjectGroups: function() {
      this.subGroupSpinning = true
      this.$http.get('/api/subject-group')
        .then(({ data }) => {
          this.subjectGroups = data.data
          this.subGroupSpinning = false
        })
    },
    handleSessionChange(value) {
      if (value) {
        this.selectedSession = value
      } else {
        this.selectedSession = this.currentSession
      }

      this.getClasses()
    },
    handleClassChange(value) {
      this.classFees = []
      if (value) {
        this.feeSpinning = true
        this.$http
          .get('/api/class_fees', {
            params: {
              session_id: this.selectedSession,
              class_id: value,
            },
          })
          .then(({ data }) => {
            this.feeSpinning = false
            this.classFees = data.data
            console.log(data.data)
          })
          .catch((error) => {
            this.feeSpinning = false
            console.log(error)
          })
      }
    },
    handleAddSubjectGrouping() {
      this.groupingCounter++
      if (this.subjectGroupings.length < 10) {
        this.subjectGroupings.push(this.groupingCounter)
      }
    },
    handleRemoveSubjectGrouping(key) {
      this.$confirm({
        title: `Are you sure?`,
        content: `Click Ok to remove this subject grouping.`,
        onOk: () => {
          this.subjectGroupings = this.subjectGroupings.filter(
            (item) => item !== key
          )
        },
        onCancel: () => {
          this.loading = false
        },
      })
    },
    handleSubmit(e) {
      e.preventDefault()
      this.loading = true
      this.form.validateFields((err, values) => {
        if (!err) {
          this.$confirm({
            title: `Are you sure?`,
            content: `Click on the OK button to generate student fees!`,
            onOk: () => {
              let onlineApplicationFormData =
                this.makeOnlineApplicationFormData(values)
              // const ids = []
              const subGroupObjectlist = []
              onlineApplicationFormData.subject_group_master_id.forEach((value, index1) => {
                subGroupObjectlist.push({
                  sub_grp_master_id: value,
                  subjectIds: onlineApplicationFormData.subject_group[index1 - 1],
                })
              })
              onlineApplicationFormData.subject_group = subGroupObjectlist
              this.$http
                .post('/api/online-application', onlineApplicationFormData)
                .then(({ data }) => {
                  this.$emit('created')
                  this.loading = false
                  this.visible = false
                  this.$notification['success']({
                    message: 'Application form created successfully!',
                    description: `The application form has been successfully created.`,
                  })
                  this.form.resetFields()
                })
                .catch((error) => {
                  this.loading = false
                  this.$neoNotify.error(error)
                })
            },
            onCancel: () => {
              this.loading = false
            },
          })
        }

        this.loading = false
      })
    },
    makeOnlineApplicationFormData(values) {
      let returnData = values
      returnData.start_date = moment(values.start_date).format('YYYY-MM-DD')
      returnData.end_date = moment(values.end_date).format('YYYY-MM-DD')
      returnData.admission_last_date = moment(
        values.admission_last_date
      ).format('YYYY-MM-DD')
      returnData.subject_group = values.subject_group
        ? values.subject_group.filter((item) => item)
        : ''
      return returnData
    },
    handleCancel(e) {
      this.visible = false
    },
  },
}
</script>
